import { useEffect, useRef } from 'react';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';

// Three callers, three auth models:
// - author: workpay author, session-authed, subscribes by target
// - reviewer: external reviewer (email link), token-authed, subscribes by token
// - reviewer-internal: session-authed INTERNAL reviewer (tenant user or workpay
//   colleague) for an in-portal review, subscribes by reviewId
type UseReviewSseOptions =
  | {
      mode: 'author';
      targetType: 'TEMPLATE' | 'ENVELOPE';
      targetId: string;
      enabled?: boolean;
      baseUrl?: string;
    }
  | {
      mode: 'reviewer';
      token: string;
      targetType?: 'TEMPLATE' | 'ENVELOPE';
      targetId?: string;
      enabled?: boolean;
      baseUrl?: string;
    }
  | {
      mode: 'reviewer-internal';
      reviewId: string;
      snapshotId: string;
      enabled?: boolean;
      baseUrl?: string;
    };

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const SAFE_TARGET_TYPES = new Set(['TEMPLATE', 'ENVELOPE']);

// ── Event handling ────────────────────────────────────────────────────
// Invalidations per mode. Pulled out so both onmessage and onopen (reconnect)
// can trigger the same refresh — the SSE stream has NO replay on reconnect,
// so refetching the authoritative REST endpoints covers missed events.

function invalidateForMode(opts: UseReviewSseOptions, queryClient: QueryClient, onStatusChange: boolean) {
  if (opts.mode === 'author') {
    void queryClient.invalidateQueries({ queryKey: ['ds-reviews', opts.targetType, opts.targetId] });
    if (onStatusChange) {
      if (opts.targetType === 'TEMPLATE') {
        void queryClient.invalidateQueries({ queryKey: ['dsTemplate', opts.targetId] });
      } else {
        void queryClient.invalidateQueries({ queryKey: ['dsEnvelope', opts.targetId] });
      }
    }
  } else if (opts.mode === 'reviewer') {
    if (opts.targetType && opts.targetId) {
      void queryClient.invalidateQueries({ queryKey: ['ds-reviews', opts.targetType, opts.targetId] });
    }
  } else if (opts.mode === 'reviewer-internal') {
    void queryClient.invalidateQueries({ queryKey: ['dsSnapshot-my-review', opts.snapshotId] });
    void queryClient.invalidateQueries({ queryKey: ['dsSnapshot-reviewer-activities', opts.reviewId] });
    if (onStatusChange) {
      // Snapshot row status may have changed (IN_REVIEW → DRAFT after dual-completion).
      // Invalidate both tenant + workpay cross-org keys; only one is cached.
      void queryClient.invalidateQueries({ queryKey: ['dsSnapshot', opts.snapshotId] });
      void queryClient.invalidateQueries({ queryKey: ['dsCrossOrgSnapshot', opts.snapshotId] });
    }
  }
}

function buildUrl(opts: UseReviewSseOptions, baseUrl: string): string | null {
  if (opts.mode === 'author' && opts.targetType && opts.targetId) {
    return `${baseUrl}/v1/reviews/stream?targetType=${encodeURIComponent(opts.targetType)}&targetId=${encodeURIComponent(opts.targetId)}`;
  }
  if (opts.mode === 'reviewer' && opts.token) {
    return `${baseUrl}/v1/review-session/${encodeURIComponent(opts.token)}/stream`;
  }
  if (opts.mode === 'reviewer-internal' && opts.reviewId) {
    return `${baseUrl}/v1/reviews/${encodeURIComponent(opts.reviewId)}/reviewer-stream`;
  }
  return null;
}

function validateIds(opts: UseReviewSseOptions): boolean {
  if (opts.mode === 'author') {
    if (!UUID_RE.test(opts.targetId)) return false;
    if (!SAFE_TARGET_TYPES.has(opts.targetType)) return false;
  } else if (opts.mode === 'reviewer') {
    if (opts.targetId && !UUID_RE.test(opts.targetId)) return false;
    if (opts.targetType && !SAFE_TARGET_TYPES.has(opts.targetType)) return false;
  } else if (opts.mode === 'reviewer-internal') {
    if (!UUID_RE.test(opts.reviewId)) return false;
    if (!UUID_RE.test(opts.snapshotId)) return false;
  }
  return true;
}

export function useReviewSse(opts: UseReviewSseOptions) {
  const { enabled = true, baseUrl = '/api/contract-service' } = opts;
  const queryClient = useQueryClient();
  const retryCountRef = useRef(0);
  // Prevent double-fetch on initial connection — the consumer queries
  // are already in flight from mount. Only invalidate on true reconnects.
  const isFirstOpenRef = useRef(true);

  useEffect(() => {
    if (!enabled) return;
    if (!validateIds(opts)) return;

    const url = buildUrl(opts, baseUrl);
    if (!url) return;

    let es: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;

    function connect() {
      es = new EventSource(url!);

      es.onopen = () => {
        retryCountRef.current = 0;
        if (isFirstOpenRef.current) {
          isFirstOpenRef.current = false;
          return;
        }
        // Reconnect: refetch authoritative state. SSE has no replay —
        // any events dropped during the disconnect window are gone from
        // the stream. The REST endpoints always return the full current state.
        invalidateForMode(opts, queryClient, true);
      };

      es.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data as string) as Record<string, unknown>;
          const eventType = data.type;

          if (eventType === 'activity_added' || eventType === 'reviewer_status_changed') {
            invalidateForMode(opts, queryClient, false);
          }
          if (eventType === 'review_status_changed') {
            invalidateForMode(opts, queryClient, true);
          }
        } catch {
          /* ignore parse errors */
        }
      };

      es.onerror = () => {
        es?.close();
        const delays = [5000, 15000, 30000];
        const delay = delays[Math.min(retryCountRef.current, delays.length - 1)]!;
        retryCountRef.current++;
        retryTimer = setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      es?.close();
      if (retryTimer) clearTimeout(retryTimer);
      // Reset the first-open guard when the effect tears down so a remount
      // (e.g. changing reviewId) starts a fresh session cleanly.
      isFirstOpenRef.current = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    opts.mode,
    // Stable deps per mode — extract only primitive fields that drive URL identity
    'targetType' in opts ? opts.targetType : undefined,
    'targetId' in opts ? opts.targetId : undefined,
    'token' in opts ? opts.token : undefined,
    'reviewId' in opts ? opts.reviewId : undefined,
    'snapshotId' in opts ? opts.snapshotId : undefined,
    enabled,
    baseUrl,
    queryClient,
  ]);
}
